import { Component, Inject, signal, Signal, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { RegionSettings, UpdateRegionSettingsMutation } from 'gql/graphql';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { RegionSettingsFormComponent } from '../general-forms/region-settings-form.component';
import { regionSettingsMutation } from '@graphql/general';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { getErrorMessage } from '@utils/errors';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';

export type RegionSettingsModalData = {
  regionSettings$?: BehaviorSubject<RegionSettings | null>;
  webBrowserTime?: Date;
  currentBrowserTimezone?: string;
};

@Component({
  selector: 'abb-region-settings-modal',
  standalone: true,
  template: `
    <abb-modal [title]="'Edit' | t" #modalRef>
      <abb-region-settings-form
        [initValues]="initValues"
        (formSubmit)="onSubmit($event)"
        (formValidationStatusChange)="handleSubmitStatusBtn($event)"
      ></abb-region-settings-form>
      <div footer class="w-full flex flex-row gap-8 justify-end">
        <abb-button variant="clean" (onclick)="onCancel()">{{
          'cancel' | t
        }}</abb-button>
        <abb-button
          class="w-32"
          type="submit"
          form="region-settings-form"
          [loading]="saving()"
          [disabled]="submitBtnDisabled || saving()"
          >{{ 'Save' | t }}</abb-button
        >
      </div>
    </abb-modal>
  `,
  styles: ``,
  imports: [
    AbbModalComponent,
    TPipe,
    AbbButtonComponent,
    RegionSettingsFormComponent,
  ],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.general' },
  ],
  host: { class: 'flex flex-1 min-h-0 flex-col' },
})
export class RegionSettingsModalComponent
  implements ModalData<RegionSettingsModalData>
{
  @ViewChild('modalRef') modalRef: AbbModalComponent | undefined;

  user: Signal<UserWithID | null>;
  private subscriptions: Subscription[] = [];
  constructor(
    @Inject(MODAL_DATA) public data: RegionSettingsModalData,
    private readonly cognitoService: CognitoService,
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    this.user = cognitoService.loggedUser;
  }

  readonly initValues = {
    webBrowserTime: this.data.webBrowserTime!,
    currentBrowserTimezone: this.data.currentBrowserTimezone!,
    deviceTime: this.data.regionSettings$?.value?.deviceTime,
    timezone: this.data.regionSettings$?.value?.timezone!,
    currency: this.data.regionSettings$?.value?.currency!,
  };

  submitBtnDisabled: boolean = true;
  handleSubmitStatusBtn(disabled: boolean): void {
    this.submitBtnDisabled = disabled;
  }

  onCancel() {
    this.modalRef?.dispose();
  }

  saving = signal(false);
  onSubmit(form: FormGroup): void {
    if (form.invalid) {
      return;
    }
    this.saving.set(true);
    this.subscriptions.push(
      this.apollo
        .mutate<UpdateRegionSettingsMutation>({
          mutation: regionSettingsMutation,
          variables: {
            currencyId: form.value.currency,
            timezoneId: form.value.timezone
          },
        })
        .subscribe({
          next: ({ data }) => {
            this.apollo.client.refetchQueries({
              include: ['RegionSettings']
            })
            this.saving.set(false);
            this.modalRef?.dispose();
          },
          error: (err) => {
            this.saving.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }
}
